import { useImperativeHandle, forwardRef, useRef } from 'react';
import { Organization } from 'src/trpc';
import { OrganizationForm, OrganizationFormRef } from 'src/pages/Organization/OrganizationForm';
import { organizationZ, OrderAcceptanceModes } from '@libs/schemas/src/organization';
import { z } from 'zod';

// Extend the organization schema to include optional fields needed for the form
const formOrganizationZ = organizationZ.extend({
  _id: z.string().optional(),
});

type OrganizationZ = z.infer<typeof formOrganizationZ>;

interface OrganizationOverviewTabProps {
  organization: Organization;
  onDataChange: (hasChanges: boolean) => void;
  onSaveSuccess?: () => void;
}

export interface OrganizationOverviewTabRef {
  save: () => Promise<void>;
}

export const OrganizationOverviewTab = forwardRef<
  OrganizationOverviewTabRef,
  OrganizationOverviewTabProps
>(({ organization, onDataChange, onSaveSuccess }, ref) => {
  const formRef = useRef<OrganizationFormRef>(null);

  // Transform organization data for the form
  const initialData: OrganizationZ = {
    name: organization.name,
    status: organization.status,
    timezone: organization.timezone ?? 'America/Denver',
    fromEmail: organization.fromEmail,
    toEmail: organization.toEmail,
    adminBccEmail: organization.adminBccEmail,
    pricePerTonInCents: organization.pricePerTonInCents ?? 0,
    minimumOrderWeightInTons: organization.minimumOrderWeightInTons ?? 0,
    defaultRentalDays: organization.defaultRentalDays ?? 1,
    collectTaxes: organization.collectTaxes ?? true,
    stripeConnectAccountId: organization.stripeConnectAccountId,
    googleReviewUrl: organization.googleReviewUrl,
    minimumDistanceThresholdInMiles: organization.minimumDistanceThresholdInMiles ?? 0,
    distanceIncrementInMiles: organization.distanceIncrementInMiles ?? 0,
    surchargePerIncrementInCents: organization.surchargePerIncrementInCents ?? 0,
    minimumSurchargeInCents: organization.minimumSurchargeInCents ?? 0,
    sameDayPickupCutoffHour: organization.sameDayPickupCutoffHour ?? 12,
    hotjarSurveyEventName: organization.hotjarSurveyEventName ?? '',
    sameDayAddOnId: organization.sameDayAddOnId ?? '',
    reminderCutoffHour: organization.reminderCutoffHour ?? 12,
    orderAcceptanceMode: organization.orderAcceptanceMode ?? OrderAcceptanceModes.OPEN,
    _id: organization._id,
  };

  // Expose save function to parent component
  useImperativeHandle(ref, () => ({
    save: async () => {
      if (formRef.current) {
        await formRef.current.save();
      }
    },
  }));

  const handleStateChange = (state: { isValid: boolean; isDirty: boolean }) => {
    // Now we can properly track form changes
    onDataChange(state.isDirty);
  };

  return (
    <OrganizationForm
      ref={formRef}
      initialData={initialData}
      onSuccess={onSaveSuccess}
      onStateChange={handleStateChange}
    />
  );
});

OrganizationOverviewTab.displayName = 'OrganizationOverviewTab';
